/**
 * What has happened on this branch, as GitLab reports it.
 *
 * This screen used to say history was not available "until GET
 * /api/projects/{slug}/commits?ref= exists". It exists - projects.py:453,
 * a passthrough to GitLab - and `api.commits` has been calling distance from
 * it in the client for as long. Only the UI still denied it, which is the worst
 * shape a stale message can take: it names the exact thing that would fix it,
 * so anybody who read it went and looked for work that was already done.
 *
 * Presentational, the same rule RequirementGrid follows: given commits, it
 * draws them. What to ask for is which branch the person is on, and the screen
 * is what knows that.
 *
 * Every commit links out to GitLab rather than opening a diff here, and this is
 * NOT a first step towards one. BEST is not the record of what happened to the
 * files - GitLab is.
 *
 * THAT SEPARATION HELD. The in-app change list (#30) was built and it is
 * ChangeList.tsx, a sibling of this file rather than a growth of it: it compares
 * the branch's MODEL against the target's and answers "which elements changed",
 * where this answers "what landed on the branch". The two are drawn as two
 * panels on the Version control screen. Do not grow one into the other.
 *
 * One prediction this comment made was wrong and is worth recording. It expected
 * the change list to be built from per-element `@Author` / `@ChangedOn` metadata
 * (#29). It is not: those stamps say WHO last touched an element and WHEN, and
 * neither can say what the element used to be. The comparison is between two
 * ingested models. What #29 actually contributed is the `element_uid`, which is
 * what lets a rename read as a rename instead of as a delete beside an add.
 */

import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import HistoryIcon from '@mui/icons-material/History'
import Link from '@mui/material/Link'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import type { ApiError } from '../../api'
import type { BranchedFrom, CommitRow } from '../../schema'
import { MONOSPACE } from '../../theme'
import EmptyState from './EmptyState'

/**
 * When a commit was made, in the reader's own locale.
 *
 * GitLab answers ISO 8601 with an offset. Rendered as a local date and time
 * rather than "3 hours ago": this screen is read while deciding whether the
 * branch holds what you think it holds, and a relative time cannot be compared
 * against the timestamp on anything else.
 *
 * An unparseable value renders as itself. A blank cell would say the commit has
 * no date, which is a different and untrue claim.
 */
export function commitTime(iso: string): string {
  const when = new Date(iso)
  return Number.isNaN(when.getTime()) ? iso : when.toLocaleString()
}

export interface CommitHistoryProps {
  /** The branch these commits are from, for the heading. */
  branch: string
  commits: CommitRow[]
  loading: boolean
  error: ApiError | null
  /**
   * The commit a work branch was cut from, which ends its list. Null for the
   * default branch, which was cut from nothing.
   */
  branchedFrom?: BranchedFrom | null
  /** The branch it was cut from, named on the closing row. */
  target?: string
}

/**
 * The closing row of a work branch's history: where it left the branch it was
 * cut from. Everything above it is the branch's own; this is the last thing it
 * shares.
 */
function BranchedFromRow({
  from,
  target,
}: {
  from: BranchedFrom
  target: string
}) {
  return (
    <ListItem sx={{ display: 'block', py: 1.25, px: 2, bgcolor: 'action.hover' }}>
      <Stack
        direction="row"
        spacing={0.75}
        sx={{ alignItems: 'baseline', flexWrap: 'wrap' }}
      >
        <Typography variant="body2">Branched from</Typography>
        <Link
          href={from.web_url}
          target="_blank"
          rel="noreferrer"
          sx={{ fontFamily: MONOSPACE, fontSize: '0.85rem', flexShrink: 0 }}
        >
          {from.short_sha}
        </Link>
        <Typography variant="body2">
          on {target || 'the default branch'}, {commitTime(from.authored_at)}
        </Typography>
      </Stack>
      <Typography variant="caption" color="text.secondary">
        {from.title}
      </Typography>
    </ListItem>
  )
}

export default function CommitHistory({
  branch,
  commits,
  loading,
  error,
  branchedFrom = null,
  target = '',
}: CommitHistoryProps) {
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={28} />
      </Box>
    )
  }

  if (error) {
    // A read failure, not an empty branch. Said as an error because the two
    // look identical on screen and only one of them is worth retrying.
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error.message}
      </Alert>
    )
  }

  if (commits.length === 0) {
    return (
      <>
        <EmptyState
          icon={<HistoryIcon />}
          title="Nothing committed on this branch yet"
          description={`${branch || 'This branch'} is at the same commit as the branch it was cut from. Saving a change to the model is what puts a commit on it.`}
        />
        {branchedFrom ? (
          <List dense disablePadding>
            <BranchedFromRow from={branchedFrom} target={target} />
          </List>
        ) : null}
      </>
    )
  }

  return (
    <List dense disablePadding>
      {commits.map((commit) => (
        <ListItem
          key={commit.sha}
          divider
          sx={{ display: 'block', py: 1.25, px: 2 }}
        >
          <Stack
            direction="row"
            spacing={1.5}
            sx={{ alignItems: 'baseline', flexWrap: 'wrap' }}
          >
            <Link
              href={commit.web_url}
              target="_blank"
              rel="noreferrer"
              sx={{ fontFamily: MONOSPACE, fontSize: '0.85rem', flexShrink: 0 }}
            >
              {commit.short_sha}
            </Link>
            <Typography variant="body2" sx={{ flexGrow: 1, minWidth: 0 }}>
              {commit.title}
            </Typography>
          </Stack>
          <Typography variant="caption" color="text.secondary">
            {commit.author_name} — {commitTime(commit.authored_at)}
          </Typography>
        </ListItem>
      ))}
      {branchedFrom ? (
        <BranchedFromRow from={branchedFrom} target={target} />
      ) : null}
    </List>
  )
}
